import React from 'react';
import { useBetting } from '../../context/BettingContext';
import { useNavigate, useParams } from 'react-router-dom';
import Avatar from '../shared/Avatar';
import './EventOverview.css';

function RoundCard({ round, markets, bets }) {
  const roundMarkets = markets.filter((m) => m.roundId === round.id);
  const settled = roundMarkets.filter((m) => m.status === 'settled').length;
  const total = roundMarkets.length;
  const roundBets = bets.filter((b) => roundMarkets.some((m) => m.id === b.marketId));

  const statusBadge = {
    upcoming: { label: 'Upcoming', cls: 'eo-badge--upcoming' },
    open: { label: 'Open for Betting', cls: 'eo-badge--open' },
    closed: { label: 'Betting Closed', cls: 'eo-badge--closed' },
    concluded: { label: 'Concluded', cls: 'eo-badge--concluded' },
  }[round.status] || { label: round.status, cls: '' };

  return (
    <div className={`eo-round ${round.status === 'open' ? 'eo-round--active' : ''}`}>
      <div className="eo-round-header">
        <div>
          <span className="eo-round-number">Round {round.roundNumber}</span>
          <h4 className="eo-round-name">{round.name}</h4>
        </div>
        <span className={`eo-badge ${statusBadge.cls}`}>{statusBadge.label}</span>
      </div>
      <div className="eo-round-stats">
        <div className="eo-round-stat">
          <span className="eo-round-stat-val">{total}</span>
          <span className="eo-round-stat-lbl">Markets</span>
        </div>
        <div className="eo-round-stat">
          <span className="eo-round-stat-val">{settled}/{total}</span>
          <span className="eo-round-stat-lbl">Settled</span>
        </div>
        <div className="eo-round-stat">
          <span className="eo-round-stat-val">{roundBets.length}</span>
          <span className="eo-round-stat-lbl">Total Bets</span>
        </div>
      </div>
      {round.startDate && (
        <div className="eo-round-dates">
          {new Date(round.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          {round.endDate && <> — {new Date(round.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
        </div>
      )}
      {round.carryOver === false && round.status === 'concluded' && (
        <div className="eo-round-reset">Credits were reset after this round</div>
      )}
    </div>
  );
}

function StandingsTable({ event }) {
  const { standings } = useBetting();
  const sorted = standings.map(s => ({ ...s, displayName: s.name }));

  return (
    <div className="eo-standings">
      <h3>Current Standings</h3>
      <div className="eo-standings-list">
        {sorted.map((user, idx) => (
          <div key={user.id} className="eo-standings-row">
            <span className="eo-standings-rank">
              {idx === 0 ? '👑' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
            </span>
            <Avatar user={user} size={18} className="eo-standings-avatar" />
            <span className="eo-standings-name">{user.displayName}</span>
            <span className="eo-standings-balance">{user.balance.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function EventOverview() {
  const { id: masterEventId, actId } = useParams();
  const { events, rounds, markets, bets } = useBetting();
  const navigate = useNavigate();

  const activeEvent = events.find((e) => e.status === 'active');
  const concludedEvents = events.filter((e) => e.status === 'concluded');

  return (
    <div className="eo-page">
      <div className="eo-header">
        <h2>Events & Rounds</h2>
        <p className="eo-header-sub">Track the competition across all events and rounds</p>
      </div>

      {activeEvent ? (
        <div className="eo-event eo-event--active">
          <div className="eo-event-header">
            <div>
              <span className="eo-badge eo-badge--open">Active</span>
              <h3 className="eo-event-name">{activeEvent.name}</h3>
              <p className="eo-event-meta">
                {activeEvent.totalRounds} rounds — Starting credits: {activeEvent.startingCredits?.toLocaleString()} coins
              </p>
            </div>
            <button className="eo-bet-btn" onClick={() => navigate(`/me/${masterEventId}/activity/${actId}/betting/place`)}>
              Place Bets →
            </button>
          </div>

          <div className="eo-event-body">
            <div className="eo-rounds-list">
              <h3>Rounds</h3>
              {rounds
                .filter((r) => r.eventId === activeEvent.id)
                .sort((a, b) => a.roundNumber - b.roundNumber)
                .map((round) => (
                  <RoundCard key={round.id} round={round} markets={markets} bets={bets} />
                ))}
            </div>
            <StandingsTable event={activeEvent} />
          </div>
        </div>
      ) : (
        <div className="eo-empty">
          <span className="eo-empty-icon">📋</span>
          <p>No active event right now. Ask the admin to create one!</p>
        </div>
      )}

      {concludedEvents.length > 0 && (
        <div className="eo-concluded">
          <h3>Past Events</h3>
          {concludedEvents.map((evt) => {
            const evtRounds = rounds.filter((r) => r.eventId === evt.id);
            return (
              <div key={evt.id} className="eo-event eo-event--concluded">
                <div className="eo-event-header">
                  <div>
                    <span className="eo-badge eo-badge--concluded">Concluded</span>
                    <h3 className="eo-event-name">{evt.name}</h3>
                    <p className="eo-event-meta">
                      {evtRounds.length} rounds completed
                      {evt.concludedAt && <> — {new Date(evt.concludedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
